import { CreateUserRequest, CreateUserResponse, DeleteUserRequest, DeleteUserResponse, PatchUserRequest, PatchUserResponse } from './protos/user_pb';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';
import { AvatarCode, IAccountsDomain, createUserObject } from './types';
import { ResultKind } from 'services/base/types';
import { AxiosResponse } from 'axios';
import { runInAction } from 'mobx';
import { sharedApplySessionResponse, sharedClearSessionResponse } from './session_service';
import { FCMService } from './fcm_service';

export type CreateResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.SYSTEM_ERROR }
  | {
    kind: ResultKind.USER_ERROR;
    emailError?: string;
    passwordError?: string;
    confirmPasswordError?: string;
    countryError?: string;
    mobileNumberPrefixError?: string;
    mobileNumberError?: string;
  };


export type CreateUserRequestProps = {
  email: string;
  password: string;
  confirmPassword: string;
  country: string;
  mobileNumberPrefix: string;
  mobileNumber: string;
  preferredLanguage: string;
};

export type PostUserProps = { domain: IAccountsDomain } & CreateUserRequestProps;

export const post = async(args: PostUserProps): Promise<CreateResult> => {
  const {
    domain,
    ...createUserRequestProps
  } = args;

  try {
    const request = createCreateUserRequest(createUserRequestProps);
    const httpResp = await http.post('/user/', createRequestString(request));
    return runInAction(() => {
      applyCreateUserResponse(domain, httpResp);
      FCMService.update();
      return { kind: ResultKind.OK };
    });
  } catch (error) {
    if (isHttpError(error)) {
      if (error.response?.status && error.response.status < 500) {
        const httpResp = error.response;
        const response = CreateUserResponse.deserializeBinary(getResponseBytes(httpResp));
        return {
          kind: ResultKind.USER_ERROR,
          emailError: response.getEmailError(),
          passwordError: response.getPasswordError(),
          confirmPasswordError: response.getConfirmPasswordError(),
          countryError: response.getCountryError(),
          mobileNumberPrefixError: response.getMobileNumberPrefixError(),
          mobileNumberError: response.getMobileNumberError(),
        };
      }
    }
    return { kind: ResultKind.SYSTEM_ERROR };
  }
};

export type PatchUserRequestProps = {
  // General user information
  email?: string;
  country?: string;
  mobileNumberPrefix?: string;
  mobileNumber?: string;
  avatarCode?: AvatarCode;

  // Change password
  oldPassword?: string;
  password?: string;
  confirmPassword?: string;

  // Intro completed
  introCompleted?: boolean;

  // Push notifications
  pushNotifications?: boolean;

  // Push notifications
  preferredLanguage?: string;
};

export type PatchUserProps = { domain: IAccountsDomain } & PatchUserRequestProps;

export type PatchResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.SYSTEM_ERROR }
  | {
    kind: ResultKind.USER_ERROR;
    emailError?: string;
    countryError?: string;
    mobileNumberPrefixError?: string;
    mobileNumberError?: string;
    avatarCodeError?: string;

    oldPasswordError?: string;
    passwordError?: string;
    confirmPasswordError?: string;

    introCompletedError?: string;
    pushNotificationsError?: string;
  };

export const patch = async(args: PatchUserProps): Promise<PatchResult> => {
  const {
    domain,
    ...patchUserRequestProps
  } = args;

  try {
    const request = createPatchUserRequest(patchUserRequestProps);
    const httpResp = await http.patch('/user/', createRequestString(request));
    runInAction(() => {
      applyPatchUserResponse(domain, httpResp);
    });
    return { kind: ResultKind.OK };
  } catch (error) {
    if (isHttpError(error)) {
      if (error.response?.status && error.response.status < 500) {
        const httpResp = error.response;
        const response = PatchUserResponse.deserializeBinary(getResponseBytes(httpResp));
        return {
          kind: ResultKind.USER_ERROR,
          emailError: response.getEmailError(),
          countryError: response.getCountryError(),
          mobileNumberPrefixError: response.getMobileNumberPrefixError(),
          mobileNumberError: response.getMobileNumberError(),
          avatarCodeError: response.getAvatarCodeError(),
          oldPasswordError: response.getOldPasswordError(),
          passwordError: response.getPasswordError(),
          confirmPasswordError: response.getConfirmPasswordError(),
          introCompletedError: response.getIntroCompletedError(),
          pushNotificationsError: response.getPushNotificationsError(),
        };
      }
    }
    return { kind: ResultKind.SYSTEM_ERROR };
  }
};

export type DeleteUserRequestProps = {
  password: string;
};

export type DeleteUserProps = { domain: IAccountsDomain } & DeleteUserRequestProps;

export type DeleteUserResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.SYSTEM_ERROR }
  | {
    kind: ResultKind.USER_ERROR;
    passwordError?: string;
  };

export const deleteUser = async(args: DeleteUserProps): Promise<DeleteUserResult> => {
  const {
    domain,
    ...deleteUserRequestProps
  } = args;
  try {
    const request = createDeleteUserRequest(deleteUserRequestProps);
    const httpResp = await http.delete('/user/', { data: createRequestString(request) });
    runInAction(() => {
      applyDeleteUserResponse(domain, httpResp);
    });
    return { kind: ResultKind.OK };
  } catch (error) {
    if (isHttpError(error)) {
      if (error.response?.status && error.response.status < 500) {
        const httpResp = error.response;
        const response = DeleteUserResponse.deserializeBinary(getResponseBytes(httpResp));
        return {
          kind: ResultKind.USER_ERROR,
          passwordError: response.getPasswordError(),
        };
      }
    }
    return { kind: ResultKind.SYSTEM_ERROR };
  }
};

function applyCreateUserResponse(domain: IAccountsDomain, httpResp: AxiosResponse) {
  const response = CreateUserResponse.deserializeBinary(getResponseBytes(httpResp));
  const sessionResponse = response.getSession();
  if (sessionResponse != null) {
    sharedApplySessionResponse(domain, sessionResponse);
  }
}

function createCreateUserRequest(props: CreateUserRequestProps): CreateUserRequest {
  const request = new CreateUserRequest();
  request.setEmail(props.email.trim());
  request.setPassword(props.password);
  request.setConfirmPassword(props.confirmPassword);
  request.setCountry(props.country);
  request.setMobileNumberPrefix(props.mobileNumberPrefix);
  request.setMobileNumber(props.mobileNumber);
  request.setPreferredLanguage(props.preferredLanguage);
  return request;
}

function applyPatchUserResponse(domain: IAccountsDomain, httpResp: AxiosResponse) {
  const response = PatchUserResponse.deserializeBinary(getResponseBytes(httpResp));
  const userDTO = response.getUser();
  domain.user = userDTO ? createUserObject(userDTO) : undefined;
}

function createPatchUserRequest(props: PatchUserRequestProps): CreateUserRequest {
  const request = new PatchUserRequest();

  // General user information
  if (props.email != null) {
    request.setEmail(props.email.trim());
  }
  if (props.country != null) {
    request.setCountry(props.country);
  }
  if (props.mobileNumberPrefix != null) {
    request.setMobileNumberPrefix(props.mobileNumberPrefix);
  }
  if (props.mobileNumber != null) {
    request.setMobileNumber(props.mobileNumber);
  }
  if (props.avatarCode != null) {
    request.setAvatarCode(props.avatarCode);
  }

  // Change password
  if (props.oldPassword != null) {
    request.setOldPassword(props.oldPassword);
  }
  if (props.password != null) {
    request.setPassword(props.password);
  }
  if (props.confirmPassword != null) {
    request.setConfirmPassword(props.confirmPassword);
  }

  // Intro completed
  if (props.introCompleted != null) {
    request.setIntroCompleted(props.introCompleted);
  }

  // Push notification
  if (props.pushNotifications != null) {
    request.setPushNotifications(props.pushNotifications);
  }

  // Preferred language
  if (props.preferredLanguage != null) {
    request.setPreferredLanguage(props.preferredLanguage);
  }

  return request;
}

function createDeleteUserRequest(props: DeleteUserRequestProps): DeleteUserRequest {
  const request = new DeleteUserRequest();
  request.setPassword(props.password);
  return request;
}

function applyDeleteUserResponse(domain: IAccountsDomain, httpResp: AxiosResponse) {
  const response = DeleteUserResponse.deserializeBinary(getResponseBytes(httpResp));
  const sessionResponse = response.getSession();
  if (sessionResponse != null) {
    sharedClearSessionResponse(domain, sessionResponse);
  }
}
